import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http, errMsg, API } from "../../lib/api";
import { PageImg } from "./PageImg";

const TIERS = ["mini-break", "premium", "ultra", "elite"];
const inp = "w-24 rounded-sm border border-stone-300 px-2 py-1 text-sm";

export const BatchBuilder = () => {
  const [d, setD] = useState(null);
  useEffect(() => { http.get("/ops/batch").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const save = async () => {
    try { await http.post("/ops/batch", { enabled: d.enabled, per_night: Number(d.per_night), edition: d.edition, region: d.region }); toast.success("Batch builder saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="batch-builder" className="max-w-xl space-y-3 text-sm">
      <p className="text-stone-600">Each night at 1am (Sydney) the builder queues this many destinations from your register. It builds and page-checks them, and fills light pages on the new kits. You then approve them on the Pipeline board. {d.waiting} destination(s) haven't been built yet.</p>
      <label className="flex items-center gap-2"><input data-testid="batch-enabled" type="checkbox" checked={d.enabled} onChange={(e) => setD({ ...d, enabled: e.target.checked })} /> Build kits every night</label>
      <label className="flex items-center gap-2">Kits a night <input data-testid="batch-per-night" type="number" min={1} max={20} value={d.per_night} onChange={(e) => setD({ ...d, per_night: e.target.value })} className={inp} /></label>
      <label className="flex items-center gap-2">Edition <select data-testid="batch-edition" value={d.edition} onChange={(e) => setD({ ...d, edition: e.target.value })} className="rounded-sm border border-stone-300 px-2 py-1">
        <option value="">Any</option>{["wine", "adventure", "escape", "solo", "luxury"].map((x) => <option key={x} value={x}>{x}</option>)}</select></label>
      <label className="flex items-center gap-2">Region <select data-testid="batch-region" value={d.region} onChange={(e) => setD({ ...d, region: e.target.value })} className="rounded-sm border border-stone-300 px-2 py-1">
        <option value="">Any</option><option value="Australia/Pacific">Australia/Pacific</option><option value="Global">Global</option></select></label>
      {d.last_run && <p className="text-xs text-stone-500">Last night: {d.last_count} kit(s) queued · {new Date(d.last_run).toLocaleString()}</p>}
      {d.nightly?.finished_at && <p className="text-xs text-stone-500">Last nightly check: {d.nightly.checked} live kit(s) checked, {d.nightly.flagged} flagged</p>}
      <button data-testid="batch-save" onClick={save} className="rounded-sm bg-[#2B2A26] px-4 py-2 text-xs font-bold text-white hover:bg-black">Save</button>
    </div>
  );
};

export const PriceList = () => {
  const [d, setD] = useState(null);
  useEffect(() => { http.get("/ops/price-list").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const set = (grp, t, v) => setD({ ...d, [grp]: { ...d[grp], [t]: v } });
  const save = async () => {
    try {
      const num = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, Number(v)]));
      const r = await http.post("/ops/price-list", { researched: num(d.researched), founders: num(d.founders), standalone: Number(d.standalone), guide: Number(d.guide), footer: d.footer });
      toast.success(`Price list saved · ${r.data.kits_repriced} kit price(s) updated · PDFs use it from their next rebuild`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="price-list" className="max-w-2xl space-y-4 text-sm">
      <p className="text-stone-600">One list used by the shop, every collection page and every PDF.</p>
      {["researched", "founders"].map((g) => (
        <div key={g}><b className="text-[#2B2A26]">{g === "researched" ? "Researched kits" : "Founder's Collection"}</b>
          <div className="mt-1 flex flex-wrap gap-4">{TIERS.map((t) => <label key={t} className="flex items-center gap-1.5 capitalize">{t} $<input data-testid={`price-${g}-${t}`} type="number" value={d[g][t]} onChange={(e) => set(g, t, e.target.value)} className={inp} /></label>)}</div>
        </div>
      ))}
      <div className="flex gap-6">
        <label className="flex items-center gap-1.5">Standalone guide $<input data-testid="price-standalone" type="number" value={d.standalone} onChange={(e) => setD({ ...d, standalone: e.target.value })} className={inp} /></label>
        <label className="flex items-center gap-1.5">Themed guide $<input data-testid="price-guide" type="number" value={d.guide} onChange={(e) => setD({ ...d, guide: e.target.value })} className={inp} /></label>
      </div>
      <label className="block">Footer line on every kit page (leave empty for the standard line)
        <input data-testid="price-footer" value={d.footer} onChange={(e) => setD({ ...d, footer: e.target.value })} placeholder="© PB Travel Angel: AI-assisted research, human-curated judgement" className="mt-1 w-full rounded-sm border border-stone-300 px-2 py-1" /></label>
      <button data-testid="price-save" onClick={save} className="rounded-sm bg-[#2B2A26] px-4 py-2 text-xs font-bold text-white hover:bg-black">Save price list</button>
    </div>
  );
};

export const LookCheck = () => {
  const [kits, setKits] = useState([]);
  useEffect(() => { http.get("/ops/pipeline").then((r) => setKits(r.data.kits.filter((k) => k.samples?.length > 1))); }, []);
  return (
    <div data-testid="look-check" className="space-y-5">
      <p className="text-sm text-stone-600">Cover, chapter opener, venue page and day page from every kit, side by side.</p>
      {kits.map((k) => (
        <div key={k.id} data-testid={`look-row-${k.id}`}>
          <div className="mb-1 text-sm"><b className="text-[#2B2A26]">{k.destination}</b> <span className="text-xs text-stone-400">{k.edition} · {k.tier}{k.collection === "founders" ? " · Founder's" : ""}</span></div>
          <div className="flex gap-2">{k.samples.map((n) => <PageImg key={n} src={`${API}/kits/${k.id}/pdf/page/${n}?thumb=1`} className="h-48 w-[136px] border border-stone-200" />)}</div>
        </div>
      ))}
    </div>
  );
};

export const WeeklyReport = () => {
  const [w, setW] = useState(null);
  useEffect(() => { http.get("/ops/weekly").then((r) => setW(r.data)); }, []);
  if (!w) return null;
  const total = w.spend.reduce((a, s) => a + s.cost, 0);
  const send = async () => { try { await http.post("/register/weekly/send"); toast.success("Weekly report emailed"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="weekly-report" className="max-w-3xl space-y-3 text-sm">
      <div className="flex gap-8">{[["Built", w.built], ["Published", w.published], ["Stopped", w.stopped.length], ["AI spend", `$${total.toFixed(2)}`], ["Balance", w.balance == null ? "?" : `$${w.balance.toFixed(2)}`]].map(([l, v]) => (
        <div key={l}><div className="text-[11px] uppercase tracking-wider text-stone-400">{l}</div><div data-testid={`weekly-${l.toLowerCase().replace(" ", "-")}`} className="font-display text-2xl text-[#2B2A26]">{v}</div></div>))}</div>
      <div><b>Over the $8 limit:</b> {w.over_limit.length ? w.over_limit.map((s) => `${s.destination} ($${s.cost.toFixed(2)})`).join(", ") : "none"}</div>
      <div><b>Stopped jobs:</b> {w.stopped.length ? w.stopped.join(", ") : "none"}</div>
      <div><b>Web check flags:</b> {w.webcheck.length ? w.webcheck.map((x) => `${x.destination} (${x.venues})`).join(", ") : "none"}</div>
      <p className="text-xs text-stone-500">This is emailed to you every Monday at 7am (Sydney) with your weekly progress.</p>
      <button data-testid="weekly-send" onClick={send} className="rounded-sm border border-stone-300 px-3 py-1.5 text-xs font-bold text-stone-700 hover:bg-stone-50">Email it to me now</button>
    </div>
  );
};
